import React, { useState, useEffect } from 'react';
import { Modal, View, Text, ScrollView, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getCurrentPartnerTerms, PartnerTermsVersion } from '../../services/partner-terms-service';
import { isSuccessful } from '../../services/api-requests';
import { notificationService } from '../../services/notification-service';

interface Props {
    visible: boolean;
    onClose: () => void;
}

/**
 * Read-only modal for reviewing accepted partner terms
 * No acceptance functionality - just for review purposes
 */
const PartnerTermsReviewModal: React.FC<Props> = ({ visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [termsData, setTermsData] = useState<PartnerTermsVersion | null>(null);

    useEffect(() => {
        if (visible) {
            fetchTerms();
        }
    }, [visible]);

    const fetchTerms = async () => {
        try {
            setLoading(true);
            const response = await getCurrentPartnerTerms('SPLIT_PARTNER_TERMS');
            if (isSuccessful(response.status) && response.payload) {
                setTermsData(response.payload as PartnerTermsVersion);
            } else {
                console.error('Failed to load terms:', response);
                notificationService.sendNotification('error', 'Unable to load terms');
                onClose();
            }
        } catch (error: any) {
            console.error('Error fetching terms:', error);
            notificationService.sendNotification('error', error?.message || 'Failed to load terms');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const htmlContent = termsData ? `
        <!DOCTYPE html>
        <html>
        <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
            <style>
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
                    padding: 16px;
                    margin: 0;
                    background-color: #1A1D23;
                    color: #FFFFFF;
                    line-height: 1.6;
                }
                h1, h2 { color: #39BEB7; }
                p { margin-bottom: 12px; }
                ul { padding-left: 20px; }
                li { margin-bottom: 8px; }
            </style>
        </head>
        <body>
            ${termsData.content}
        </body>
        </html>
    ` : '';

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>Split Partner Terms and Conditions</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <Ionicons name="close" size={24} color="#FFFFFF" />
                        </Pressable>
                    </View>

                    {loading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color="#39BEB7" />
                            <Text style={styles.loadingText}>Loading terms...</Text>
                        </View>
                    ) : (
                        <ScrollView 
                            style={styles.contentScrollView}
                            showsVerticalScrollIndicator={false}
                        >
                            {/* Terms Metadata */}
                            {termsData && (
                                <View style={styles.termsMetadata}>
                                    <View style={styles.metadataRow}>
                                        <Ionicons name="document-text" size={16} color="#39BEB7" />
                                        <Text style={styles.metadataText}>Version: {termsData.version}</Text>
                                    </View>
                                    <View style={styles.metadataRow}>
                                        <Ionicons name="calendar-outline" size={16} color="#39BEB7" />
                                        <Text style={styles.metadataText}>
                                            Effective Date: {new Date(termsData.effectiveDate).toLocaleDateString()}
                                        </Text>
                                    </View>
                                </View>
                            )}

                            {/* HTML Content in iframe */}
                            {termsData && (
                                <View style={styles.iframeContainer}>
                                    <iframe
                                        srcDoc={htmlContent}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            backgroundColor: '#1A1D23',
                                        }}
                                        title="Terms and Conditions"
                                    />
                                </View>
                            )}

                            {/* Info Message */}
                            <View style={styles.infoBox}>
                                <Ionicons name="information-circle" size={20} color="#39BEB7" />
                                <Text style={styles.infoText}>
                                    You have already accepted these terms. This is a read-only view for your reference.
                                </Text>
                            </View>

                            {/* Close Button */}
                            <View style={styles.actionButtons}>
                                <Pressable
                                    style={styles.closeActionButton}
                                    onPress={onClose}
                                >
                                    <Text style={styles.closeActionButtonText}>Close</Text>
                                </Pressable>
                            </View>

                            {/* Footer Note */}
                            <Text style={styles.footerNote}>
                                These are the terms you accepted for participating in the MeanderEV Revenue Share program.
                            </Text>
                        </ScrollView>
                    )}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        width: '100%',
        maxWidth: 700,
        height: '90%',
        backgroundColor: '#1A1D23',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        backgroundColor: '#23262B',
        borderBottomWidth: 1,
        borderBottomColor: '#2A2D35',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#FFFFFF',
        flex: 1,
    },
    closeButton: {
        padding: 4,
    },
    loadingContainer: {
        flex: 1,
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingText: {
        color: '#8D8E90',
        marginTop: 12,
        fontSize: 14,
    },
    contentScrollView: {
        flex: 1,
    },
    termsMetadata: {
        padding: 20,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#2A2D35',
        gap: 8,
    },
    metadataRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    metadataText: {
        color: '#8D8E90',
        fontSize: 13,
    },
    iframeContainer: {
        height: 400,
        backgroundColor: '#1A1D23',
        marginBottom: 8,
    },
    infoBox: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        marginHorizontal: 20,
        marginVertical: 16,
        backgroundColor: 'rgba(57, 190, 183, 0.1)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(57, 190, 183, 0.2)',
    },
    infoText: {
        flex: 1,
        color: '#FFFFFF',
        fontSize: 14,
        lineHeight: 20,
    },
    actionButtons: {
        padding: 20,
        paddingTop: 0,
    },
    closeActionButton: {
        backgroundColor: '#39BEB7',
        paddingVertical: 14,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    closeActionButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
    },
    footerNote: {
        color: '#8D8E90',
        fontSize: 12,
        textAlign: 'center',
        paddingHorizontal: 20,
        paddingBottom: 20,
        lineHeight: 18,
    },
});

export default PartnerTermsReviewModal;

